import { Component, Input, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { lastValueFrom, map } from 'rxjs';
import {
  EditionCustomization,
  EditionCustomizationIcons,
  EditionCustomizationRequest,
  FileControllerService,
  SubscriptionControllerService
} from 'src/app/admin-api';
import { CustomizationFormComponent } from 'src/app/components/customization/customization-form.component';
import { TableUtil } from 'src/app/components/table/table.util';
import {
  BusinessUnit,
  EditionId,
  getSubscriptionName,
  SubscriptionEnum
} from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { ImageService } from 'src/app/services/image.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  buildEditionAssetFileName,
  EDITION_VIDEOS_FILE_PATH,
  getPosterExtensionFromFile
} from './edition-asset.util';
import { assignIconEditionIds } from './edition-icons.util';

@Component({
  selector: 'app-edition',
  templateUrl: './edition.component.html',
  styleUrls: ['./edition.component.scss'],
  providers: [MessageService]
})
export class EditionComponent
  extends CustomizationFormComponent
  implements OnInit
{
  @Input()
  editionId: string;
  editionDate: number;
  businessUnitId: BusinessUnit;
  edition: EditionCustomization;
  iconCombo: string;
  iconBox: string;
  iconBag: string;
  iconPass: string;
  subscriptions: SubscriptionEnum[] = [];

  private readonly glamSubscriptions = [
    SubscriptionEnum.Glamcombo,
    SubscriptionEnum.Glambox,
    SubscriptionEnum.Glambag,
    SubscriptionEnum.Glampass,
    SubscriptionEnum['Glamcombo Premium'],
    SubscriptionEnum['Glambox Premium'],
    SubscriptionEnum['Glambag Premium']
  ];

  private readonly mensSubscriptions = [
    SubscriptionEnum.Select,
    SubscriptionEnum.Prime
  ];

  override form = new FormGroup({
    editionTheme: new FormControl<string>(null, Validators.required),
    editionDateTime: new FormControl<Date>(null, Validators.required),
    editionDate: new FormControl<number>(null, Validators.required),
    businessUnitId: new FormControl<number>(
      BusinessUnit.Glam,
      Validators.required
    ),
    editionCardBackgroundColor: new FormControl<string>('#FFFFFF'),
    editionCardDesktop: new FormControl<string>(null),
    editionCardMobile: new FormControl<string>(null),
    lastEditionsCarouselCard: new FormControl<string>(null),
    lastEditionsCarouselCardAlt: new FormControl<string>(null),
    videoUrl: new FormControl<string>(null),
    posterUrl: new FormControl<string>(null),
    icons: new FormArray<FormGroup>([])
  });

  constructor(
    private fb: FormBuilder,
    private subscriptionControllerService: SubscriptionControllerService,
    private activatedRoute: ActivatedRoute,
    override router: Router,
    override config: PrimeNGConfig,
    override imageService: ImageService,
    override fileService: FileControllerService,
    private messageService: MessageService
  ) {
    super(router, config, imageService, fileService);
  }

  async ngOnInit() {
    LoaderService.showLoader();
    this.businessUnitId = this.resolveBusinessUnitId(
      this.activatedRoute.snapshot.queryParamMap.get('businessUnitId')
    );
    this.form.get('businessUnitId')?.setValue(this.businessUnitId);
    this.subscriptions = this.getSubscriptionsByBusinessUnit();
    await this.getDocument();
    this.initializeForm();
    LoaderService.showLoader(false);
  }

  override async getDocument(): Promise<void> {
    if (this.editionId !== 'new') {
      try {
        const editonId = Number(this.editionId);
        this.edition = await lastValueFrom(
          this.subscriptionControllerService
            .getEditonCustomizationById(editonId, this.businessUnitId)
            .pipe(
              map((data) => {
                const result = data.result as EditionCustomization & {
                  videoUrl?: string;
                  posterUrl?: string;
                  VideoUrl?: string;
                  PosterUrl?: string;
                };
                return {
                  ...result,
                  editionDateTime: new Date(result.editionDateTime),
                  videoUrl: result.videoUrl ?? result.VideoUrl,
                  posterUrl: result.posterUrl ?? result.PosterUrl
                };
              })
            )
        );
      } catch (error) {
        AppDialogService.showErrorDialog(error);
        return;
      }
    }
  }

  override initializeForm(): void {
    this.setIconControls(this.edition?.icons);
    const edition = { ...(this.edition || {}) };
    delete edition.icons;

    this.form.patchValue({
      ...edition,
      businessUnitId: this.businessUnitId
    });

    this.syncIconEditionIds(this.resolveIconEditionDate());

    if (this.editionId !== 'new') this.form.get('editionDateTime')?.disable();

    this.form
      .get('editionDateTime')
      ?.valueChanges.subscribe((newValue: Date) => {
        if (newValue instanceof Date) {
          this.convertDateToEditionDate(newValue);
          this.form.get('editionDate')?.setValue(this.editionDate);
          this.syncIconEditionIds(this.editionDate);
        }
      });
  }

  override async save() {
    LoaderService.showLoader();
    try {
      this.form.get('businessUnitId')?.setValue(this.businessUnitId);
      const raw = this.form.getRawValue();
      const editionDate = this.resolveIconEditionDate();
      const payload = {
        ...raw,
        icons: editionDate
          ? assignIconEditionIds(
              raw.icons ?? [],
              this.subscriptions,
              editionDate
            )
          : raw.icons
      } as EditionCustomizationRequest & {
        videoUrl?: string;
        posterUrl?: string;
      };

      if (this.editionId === 'new') {
        const edition = await lastValueFrom(
          this.subscriptionControllerService
            .createEditionCustomization(payload)
            .pipe(map((data) => data.result))
        );
        await this.afterSubmit();
        this.messageService.add({
          severity: 'success',
          detail: 'Edição cadastrada com sucesso.',
          summary: 'Sucesso'
        });
        await this.afterSubmit();
        this.router.navigate(
          ['/configuracoes/editions/' + edition.editionDate],
          {
            queryParams: { businessUnitId: this.businessUnitId }
          }
        );
      } else {
        await lastValueFrom(
          this.subscriptionControllerService.updateEditionCustomization(payload)
        );

        this.messageService.add({
          severity: 'success',
          detail: 'Edição atualizada com sucesso.',
          summary: 'Sucesso'
        });
      }
      await this.afterSubmit();
    } catch (e) {
      AppDialogService.showErrorDialog(e);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async uploadEditionVideo(
    event: FileUploadHandlerEvent,
    pInput: FileUpload
  ): Promise<void> {
    const file = event.files?.[0];
    if (!file) return;

    if (file.type !== 'video/mp4' && !file.name?.toLowerCase().endsWith('.mp4')) {
      AppDialogService.showErrorDialog({
        message: 'O vídeo deve estar no formato MP4.'
      });
      pInput.clear();
      return;
    }

    await this.uploadEditionAsset(event, 'videoUrl', 'mp4', pInput);
  }

  async uploadEditionPoster(
    event: FileUploadHandlerEvent,
    pInput: FileUpload
  ): Promise<void> {
    const file = event.files?.[0];
    if (!file) return;

    try {
      const extension = getPosterExtensionFromFile(file);
      await this.uploadEditionAsset(event, 'posterUrl', extension, pInput);
    } catch {
      AppDialogService.showErrorDialog({
        message: 'O poster deve estar no formato PNG ou JPG.'
      });
      pInput.clear();
    }
  }

  private async uploadEditionAsset(
    event: FileUploadHandlerEvent,
    field: 'videoUrl' | 'posterUrl',
    extension: string,
    pInput: FileUpload
  ): Promise<void> {
    const editionDate =
      this.form.get('editionDate')?.value ||
      this.resolveEditionDateFromDateTime();

    if (!editionDate) {
      AppDialogService.showErrorDialog({
        message: 'Selecione o mês de vigência antes de enviar o arquivo.'
      });
      pInput.clear();
      return;
    }

    LoaderService.showLoader();
    try {
      const file = event.files[0];
      const fileName = buildEditionAssetFileName(editionDate, extension);
      const fileWithName = new File([file], fileName, {
        type: file.type || 'application/octet-stream',
        lastModified: file.lastModified
      });

      let result: string;
      if (fileWithName.size >= 32 * 1024 * 1000) {
        result = await this.uploadToBucket(
          EDITION_VIDEOS_FILE_PATH,
          fileWithName,
          fileName
        );
      } else {
        result = (await lastValueFrom(
          this.fileService
            .uploadFileForm(EDITION_VIDEOS_FILE_PATH, fileWithName, fileName)
            .pipe(map((data) => data.result))
        )) as string;
      }

      const previousUrl =
        this.model && TableUtil.deepGet(this.model, field.split('.'));
      if (previousUrl) this.imagesReplaced.push(previousUrl);

      this.filesUploaded.push(result);
      this.form.get(field)?.setValue(result);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      pInput.clear();
      LoaderService.showLoader(false);
    }
  }

  private resolveEditionDateFromDateTime(): number | null {
    const editionDateTime = this.form.getRawValue().editionDateTime;
    if (!(editionDateTime instanceof Date)) return null;

    this.convertDateToEditionDate(editionDateTime);
    this.form.get('editionDate')?.setValue(this.editionDate);
    return this.editionDate;
  }

  convertDateToEditionDate(value: Date) {
    this.editionDate = Number(
      JSON.stringify(value.getFullYear()) +
        (value.getMonth() + 1).toString().padStart(2, '0')
    );
  }

  getEditionIcon(index: number) {
    return this.icons?.controls?.at(index);
  }

  getEditionName(index: number) {
    const editionId = this.getEditionIcon(index)?.value?.editionId;
    if (!editionId) return '';
    return getSubscriptionName(EditionId.subscriptionId(editionId))?.replace(
      'Glam',
      ''
    );
  }

  private getSubscriptionsByBusinessUnit(): SubscriptionEnum[] {
    if (this.businessUnitId === BusinessUnit.Mens) return this.mensSubscriptions;
    return this.glamSubscriptions;
  }

  private resolveBusinessUnitId(value: string | null): BusinessUnit {
    const parsed = Number(value);
    return parsed === BusinessUnit.Mens
      ? BusinessUnit.Mens
      : BusinessUnit.Glam;
  }

  private setIconControls(icons: EditionCustomizationIcons[] = []): void {
    const editionDate = this.resolveIconEditionDate() ?? this.currentYearMonth();
    const controls = this.subscriptions.map(
      (subscriptionId) =>
        new FormGroup({
          editionId: new FormControl<number>(
            EditionId.fromYearMonth(subscriptionId, editionDate),
            Validators.required
          ),
          icon: new FormControl<string>(
            this.findIconBySubscription(icons, subscriptionId),
            Validators.required
          )
        })
    );
    this.form.setControl('icons', new FormArray(controls));
  }

  private syncIconEditionIds(editionDate: number | null): void {
    if (!editionDate) return;
    this.subscriptions.forEach((subscriptionId, index) => {
      this.form
        .get(`icons.${index}.editionId`)
        ?.setValue(EditionId.fromYearMonth(subscriptionId, editionDate));
    });
  }

  private resolveIconEditionDate(): number | null {
    return (
      this.form.getRawValue().editionDate ||
      this.edition?.editionDate ||
      this.editionDate ||
      null
    );
  }

  private currentYearMonth(): number {
    return EditionId.yearMonthReference(EditionId.currentEdition(0));
  }

  private findIconBySubscription(
    icons: EditionCustomizationIcons[],
    subscriptionId: SubscriptionEnum
  ): string | null {
    return (
      icons.find(
        (icon) =>
          icon.editionId &&
          EditionId.subscriptionId(icon.editionId) === subscriptionId
      )?.icon || null
    );
  }

  get icons() {
    return this.form.get('icons') as FormArray<FormGroup<any>>;
  }

  override get filePath(): string {
    return '/editions/';
  }

  override get model() {
    return this.edition;
  }

  override get modelId() {
    return this.editionId;
  }
}
